import unescapedText from 'I18n/utils/unescapedText';
import { Children } from 'react';
import { ExternalLinkIcon } from 'foundations-assets';
import { TruncateString } from '../TruncateString/TruncateString';
import devLogger from 'react-utils/devLogger';
import { isLinkUse } from './Button.helpers';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
export const getTarget = (external, target) => {
  if (target !== undefined) {
    return target;
  }
  if (external) {
    return '_blank';
  }
  return undefined;
};

/**
 * Determine the proper tabIndex based on the `disabled` and `tabIndex` props. Note that `tabIndex`
 * should not have a default value; it should only be non-null to override normal tabbing behavior.
 */
export const getTabIndex = (disabled, tabIndex, disabledValue = -1) => {
  if (typeof tabIndex === 'number') return tabIndex;
  if (disabled) {
    if (disabledValue === null) return undefined;
    return disabledValue;
  }
  return 0;
};
export const renderChildren = children => {
  let stringBuffer = '';
  let nextChildIndex = 0;
  const childArr = Children.toArray(children);
  return Children.map(children, child => {
    nextChildIndex += 1;
    if (child == null) {
      return null;
    }

    // in response to a breaking change in React 0.15
    // (https://facebook.github.io/react/blog/2016/04/07/react-v15.html#no-more-extra-ltspangts)
    // this wraps any bare strings within a <span> to provide a more
    // predictable DOM for applying automatic spacing rules.
    if (typeof child === 'string') {
      stringBuffer += child;
      if (childArr.length < nextChildIndex || typeof childArr[nextChildIndex] !== 'string') {
        const wrappedStringBuffer = /*#__PURE__*/_jsx("span", {
          children: stringBuffer
        });
        stringBuffer = '';
        return wrappedStringBuffer;
      } else {
        return null;
      }
    }
    return child;
  });
};
const StyledTruncatedContentsContainer = styled.span.withConfig({
  displayName: "Buttonutils__StyledTruncatedContentsContainer",
  componentId: "sc-1vin6w2-0"
})(({
  $isLink,
  theme
}) => {
  return Object.assign({}, !$isLink && {
    '> *:not(.sr-only) + *': {
      marginInlineStart: theme.space['200']
    }
  });
});
export const renderTruncatedContents = ({
  truncateTooltip,
  renderedChildren,
  renderedExternalButtonIcon,
  use,
  truncateStringProps
}) => /*#__PURE__*/_jsx(TruncateString, Object.assign({
  useFlex: true,
  fixedChildren: renderedExternalButtonIcon,
  tooltip: truncateTooltip || renderedChildren
  /**
   * Styles to bring this to parity w/ StyledButtonTextContainer
   */,
  justifyContent: "center"
}, truncateStringProps, {
  children: /*#__PURE__*/_jsx(StyledTruncatedContentsContainer, {
    $isLink: isLinkUse(use),
    children: renderedChildren
  })
}));
export const renderExternalButtonIcon = (external, iconProps) => {
  if (!external) return null;

  // Opening links in a new window is an accessibility anti-pattern.
  // To mitigate this, we add accessible text in the icon to warn that
  // the link will be opened in a new window
  // @link http://diveintoaccessibility.info/day_16_not_opening_new_windows.html
  return /*#__PURE__*/_jsx("span", {
    children: /*#__PURE__*/_jsx(ExternalLinkIcon, Object.assign({}, iconProps, {
      "aria-label": unescapedText('salesUI.UILink.externalTitle')
    }))
  });
};
export const warnIfShouldBeExternal = (external, computedTarget, componentName, isIconButton) => {
  if (isIconButton) {
    return;
  }
  if (!external && computedTarget && computedTarget !== '_self') {
    devLogger.warn({
      message: `${componentName}: External links should include an icon. Use \`external={true}\`.`,
      key: 'Link: should be external'
    });
  }
};